import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Link
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Lock,
  Google
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useDomain, useVisualTheme, useBranding, useLocalization } from '../context/DomainContext';

const Register = () => {
  const navigate = useNavigate();
  const { register, googleLogin } = useAuth();
  const { config } = useDomain();
  const visualTheme = useVisualTheme();
  const branding = useBranding();
  const { getText } = useLocalization();

  // Stati del form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validazione form
  const validateForm = useCallback(() => {
    if (!email || !password || !confirmPassword || !displayName) {
      setError(getText('error_fill_all_fields', 'Compila tutti i campi.'));
      return false;
    }

    if (password !== confirmPassword) {
      setError(getText('error_passwords_not_match', 'Le password non coincidono.'));
      return false;
    }

    if (password.length < 6) {
      setError(getText('error_password_too_short', 'La password deve essere di almeno 6 caratteri.'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(getText('error_invalid_email', 'Inserisci un indirizzo email valido.'));
      return false;
    }

    return true;
  }, [email, password, confirmPassword, displayName, getText]);

  // Stili memoizzati
  const inputStyles = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: visualTheme?.component_styles?.input?.border_radius || '8px',
      backgroundColor: visualTheme?.primary_colors?.background || '#ffffff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: visualTheme?.secondary_colors?.accent || '#1976d2',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: visualTheme?.secondary_colors?.accent || '#1976d2',
      }
    }
  }), [visualTheme]);

  const primaryButtonStyles = useMemo(() => ({
    backgroundColor: visualTheme?.secondary_colors?.accent || '#1976d2',
    color: '#ffffff',
    borderRadius: visualTheme?.component_styles?.button?.border_radius || '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: visualTheme?.secondary_colors?.accent || '#1565c0',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    '&:disabled': {
      backgroundColor: '#cccccc',
      color: '#666666'
    }
  }), [visualTheme]);

  const googleButtonStyles = useMemo(() => ({
    backgroundColor: '#ffffff',
    color: '#757575',
    border: '1px solid #dadce0',
    borderRadius: visualTheme?.component_styles?.button?.border_radius || '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    textTransform: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: '#cccccc'
    }
  }), [visualTheme]);

  // Handler registrazione email/password
  const handleRegister = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // CORREZIONE: Passiamo displayName come terzo parametro, NON come oggetto
      const result = await register(email, password, displayName);
      
      if (result && result.profile) {
        // Controllo se l'onboarding è necessario
        if (!result.profile.is_onboarding_completed) {
          navigate('/onboarding', { replace: true });
        } else {
          // Se onboarding è già completato, vai alla home (che mostrerà JustGo)
          navigate('/', { replace: true });
        }
      } else {
        // Se non c'è profilo ma registrazione è riuscita, vai a onboarding
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      // Gestione errori specifici Firebase
      if (err.code === 'auth/email-already-in-use') {
        setError(getText('error_email_exists', 'Questa email è già registrata.'));
      } else if (err.code === 'auth/weak-password') {
        setError(getText('error_weak_password', 'La password è troppo debole.'));
      } else if (err.code === 'auth/invalid-email') {
        setError(getText('error_invalid_email', 'Indirizzo email non valido.'));
      } else if (err.message) {
        // Mostra il messaggio di errore dal backend
        setError(err.message);
      } else {
        setError(getText('error_registration_failed', 'Errore durante la registrazione. Riprova.'));
      }
    } finally {
      setLoading(false);
    }
  }, [email, password, displayName, validateForm, register, navigate, getText]);

  // Handler registrazione Google
  const handleGoogleRegister = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await googleLogin();
      
      if (result && result.profile) {
        // Controllo se l'onboarding è necessario
        if (!result.profile.is_onboarding_completed) {
          navigate('/onboarding', { replace: true });
        } else {
          // Se onboarding è già completato, vai alla home (che mostrerà JustGo)
          navigate('/', { replace: true });
        }
      } else if (result && result.user) {
        // Utente autenticato ma senza profilo, probabilmente serve registrazione progetto
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      console.error('Google registration error:', err);
      
      // Gestione errori specifici Google
      if (err.code === 'auth/popup-closed-by-user') {
        setError(getText('error_popup_closed', 'Registrazione annullata.'));
      } else if (err.code === 'auth/popup-blocked') {
        setError(getText('error_popup_blocked', 'Popup bloccato dal browser. Abilita i popup e riprova.'));
      } else if (err.message) {
        setError(err.message);
      } else {
        setError(getText('error_google_registration_failed', 'Errore durante la registrazione con Google. Riprova.'));
      }
    } finally {
      setLoading(false);
    }
  }, [googleLogin, navigate, getText]);

  // Il resto del componente rimane identico...
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: visualTheme?.component_styles?.card?.border_radius || '12px',
          backgroundColor: visualTheme?.primary_colors?.header || '#ffffff'
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {branding?.logoUrl && (
            <Box sx={{ mb: 2 }}>
              <img
                src={branding.logoUrl}
                alt={branding.brandName || 'Logo'}
                style={{
                  maxHeight: '60px',
                  maxWidth: '200px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              color: visualTheme?.text_colors?.primary || '#1a1a1a',
              fontWeight: 700,
              fontFamily: visualTheme?.font_definitions?.primary_font || 'inherit'
            }}
          >
            {getText('register_title', 'Registrati')}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: visualTheme?.text_colors?.secondary || '#666666',
              fontFamily: visualTheme?.font_definitions?.primary_font || 'inherit'
            }}
          >
            {getText('register_subtitle', 'Crea il tuo account per iniziare')}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleRegister} sx={{ mb: 3 }}>
          {/* Nome Display */}
          <TextField
            fullWidth
            label={getText('register_display_name', 'Nome completo')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={inputStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Email */}
          <TextField
            fullWidth
            label={getText('register_email', 'Email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={inputStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Password */}
          <TextField
            fullWidth
            label={getText('register_password', 'Password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={inputStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Conferma Password */}
          <TextField
            fullWidth
            label={getText('register_confirm_password', 'Conferma password')}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={inputStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Pulsante Registrazione */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ ...primaryButtonStyles, mt: 3, mb: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              getText('register_button', 'Registrati')
            )}
          </Button>
        </Box>

        {/* Divider */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }}>
            {getText('register_or', 'oppure')}
          </Typography>
        </Divider>

        {/* Google Registration */}
        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoogleRegister}
          disabled={loading}
          sx={googleButtonStyles}
          startIcon={<Google />}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            getText('register_with_google', 'Registrati con Google')
          )}
        </Button>

        {/* Link al Login */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: visualTheme?.text_colors?.secondary || '#666666' }}>
            {getText('register_have_account', 'Hai già un account?')}{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{
                color: visualTheme?.secondary_colors?.accent || '#1976d2',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {getText('register_login_link', 'Accedi')}
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;